import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ClipboardList } from "lucide-react";
import { StatusPill } from "src/components/ui";
import { useAuthorisation } from "src/context";
import { ROUTES_CONFIG } from "src/constants";
import { POCompletionStatus } from "src/types";
import { PurchaseOrderScheduledEditBlock } from "./purchaseOrderScheduledEditBlock";
import type { EditScheduledPOApprovalSavePayload } from "./purchaseOrderScheduledEditBlock";

interface PurchaseOrderHeadingSectionProps {
  completionStatus: POCompletionStatus;
  purchaseOrderId: string;
  scheduledDate?: string;
  scheduledPOApprovalStatus?: "Pending" | "Approved" | "Rejected";
  isBackorder?: boolean;
  hasAAError?: boolean;
  onSaveScheduledEdit?: (payload: EditScheduledPOApprovalSavePayload) => void;
  isSavingScheduledEdit?: boolean;
  scheduledEditError?: Error | null;
  isEditing?: boolean;
  onEditingChange?: (value: boolean) => void;
}

const PurchaseOrderHeadingSection: React.FC<PurchaseOrderHeadingSectionProps> = ({
  completionStatus,
  purchaseOrderId,
  scheduledDate,
  scheduledPOApprovalStatus,
  isBackorder = false,
  hasAAError = false,
  onSaveScheduledEdit,
  isSavingScheduledEdit = false,
  scheduledEditError,
  isEditing,
  onEditingChange
}) => {
  const { userRole } = useAuthorisation();
  const isInternalUser = userRole !== "VENDOR";
  const showAAErrorBadge = hasAAError && isInternalUser;
  return (
    <section className="overflow-hidden rounded-2xl border border-secondary-200 shadow-soft bg-white">
      <div className="bg-gradient-to-r from-secondary-900 via-secondary-800 to-secondary-700 px-8 py-6 sm:px-10 sm:py-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-xl bg-white/10 p-3 text-white">
              <ClipboardList className="h-7 w-7" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-secondary-200">Purchase Order</p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">{purchaseOrderId}</h1>
              {purchaseOrderId?.startsWith("AA-") && (
                <span className="mt-3 inline-flex items-center rounded-full bg-warning-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-warning-800">
                  Active Ants Direct (EU)
                </span>
              )}
              {showAAErrorBadge && (
                <Link
                  to={`${ROUTES_CONFIG.resolveAAError.path}?id=${encodeURIComponent(purchaseOrderId)}`}
                  className="mt-3 ml-2 inline-flex items-center gap-1 rounded-full bg-error-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-error-800 hover:bg-error-200 transition-colors"
                  title="Internal only — click to resolve the Active Ants error"
                >
                  <AlertTriangle className="h-3 w-3" />
                  Active Ants Error · Resolve
                </Link>
              )}
              <p className="mt-3 text-sm text-secondary-100">
                Review vendor confirmations, shipments, finance records and more for this order.
              </p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-3">
            <StatusPill status={completionStatus} />

            {completionStatus === "Scheduled" && scheduledDate && (
              <PurchaseOrderScheduledEditBlock
                scheduledDate={scheduledDate}
                scheduledPOApprovalStatus={scheduledPOApprovalStatus}
                isBackorder={isBackorder}
                onSaveScheduledEdit={onSaveScheduledEdit}
                isSavingScheduledEdit={isSavingScheduledEdit}
                scheduledEditError={scheduledEditError ?? null}
                isEditing={isEditing}
                onEditingChange={onEditingChange}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export { PurchaseOrderHeadingSection };
